import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@miyuki/ui';
import {
  CircleHelpIcon,
  EllipsisVerticalIcon,
  FilePlusIcon,
  LayoutGridIcon,
  PencilIcon,
  PlayIcon,
  SaveIcon,
  SlidersHorizontalIcon,
} from 'lucide-react';

import { t } from '@/lib/i18n';

/**
 * Franja superior: dónde estoy y qué hago con el diseño entero.
 *
 * Las tres acciones que actúan sobre el diseño completo —nuevo, ajustes y
 * guardar— viven en un menú y no sueltas sobre el lienzo: cuatro íconos sin
 * texto dejaban al nombre sin lugar en un teléfono angosto y ninguno explicaba
 * qué hacía. Dentro del menú cada uno lleva su palabra, y queda sitio para la
 * guía rápida.
 *
 * «Mis diseños» se queda fuera, a un solo toque: es lo que más se usa y no
 * actúa sobre el diseño abierto (RF-24).
 */
export interface TopBarProps {
  readonly name: string;
  readonly dirty: boolean;
  /** En seguimiento la franja ofrece volver al diseño en vez de entrar (RF-03). */
  readonly tracking: boolean;
  readonly onStartTracking: () => void;
  readonly onExitTracking: () => void;
  readonly onOpenList: () => void;
  readonly onNewDesign: () => void;
  readonly onOpenSettings: () => void;
  readonly onSave: () => void;
  readonly onOpenGuide: () => void;
}

export function TopBar({
  name,
  dirty,
  tracking,
  onStartTracking,
  onExitTracking,
  onOpenList,
  onNewDesign,
  onOpenSettings,
  onSave,
  onOpenGuide,
}: TopBarProps) {
  return (
    <header className="bg-background/90 border-border flex shrink-0 items-center gap-2 border-b px-2 py-1.5 backdrop-blur">
      <Button variant="ghost" size="touch-icon" aria-label={t('myDesigns')} onClick={onOpenList}>
        <LayoutGridIcon />
      </Button>

      <h1 className="min-w-0 flex-1 truncate text-center text-sm font-medium">
        {name}
        {/* El aviso de cambios sin guardar vive fuera del menú: se ve sin abrirlo. */}
        {dirty && (
          <span className="text-muted-foreground ml-1" aria-hidden="true">
            •
          </span>
        )}
      </h1>

      {/*
        El play va suelto y no dentro del menú: es la acción que abre el modo en
        el que se pasan las horas, y esconderla a dos toques la haría parecer un
        ajuste (RF-01). Con el mismo criterio que «Mis diseños» en RF-24 de 003.
      */}
      <Button
        variant={tracking ? 'secondary' : 'ghost'}
        size="touch-icon"
        aria-label={tracking ? t('trackingExit') : t('trackingStart')}
        onClick={tracking ? onExitTracking : onStartTracking}
      >
        {tracking ? <PencilIcon /> : <PlayIcon />}
      </Button>

      {/*
        En seguimiento no se edita, así que el menú —nuevo diseño, ajustes,
        guardar— no tiene nada que ofrecer y se retira entero (RF-11).
      */}
      {!tracking && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="touch-icon" aria-label={t('menu')}>
              <EllipsisVerticalIcon />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="min-w-52">
            <DropdownMenuItem className="min-h-11 gap-3" onSelect={onNewDesign}>
              <FilePlusIcon />
              {t('newDesign')}
            </DropdownMenuItem>
            <DropdownMenuItem className="min-h-11 gap-3" onSelect={onOpenSettings}>
              <SlidersHorizontalIcon />
              {t('settings')}
            </DropdownMenuItem>
            <DropdownMenuItem className="min-h-11 gap-3" onSelect={onSave}>
              <SaveIcon />
              {t('save')}
            </DropdownMenuItem>
            <DropdownMenuItem className="min-h-11 gap-3" onSelect={onOpenGuide}>
              <CircleHelpIcon />
              {t('quickGuide')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
}
